import { DateTime } from 'luxon'
import {
  ExtensionRequestJobSubstep,
  YearEndModuleStatusOptions,
  YearEndModuleSubstepId,
  YearEndModuleType,
} from '../yearEndModuleStatus.slice'
import YearEndModuleBaseCard, {
  defaultDeadlineApproaching,
  getDeadlineApproachingDaysUntilDueDate,
  UpcomingStatusTextWrapper,
  YearEndModuleStatusDynamicDataParams,
} from './YearEndModuleStatusBaseCard'
import { GridRowColumn, Link, Text } from '@/componentsV1/BaseComponents'
import { Colors } from '../../../styles/theme'
import {
  useModuleActionButtonBehavior,
  getModuleComplementaryText,
} from '../yearEndModuleStatus.helpers'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { Grid } from 'semantic-ui-react'
import { getModuleNameCopy } from '@/features/year-end-module-status/yearEndModuleStatus.selectors'

const getWaitingOnHeardDescription = (
  substepIdentifier?: YearEndModuleSubstepId | null
) => {
  switch (substepIdentifier) {
    case ExtensionRequestJobSubstep.unclaimedJob:
      return 'We’re matching you to a tax preparer who will assist with filing your extension. Your information will be sent to them.'
    case ExtensionRequestJobSubstep.inProgress:
      return 'Your extension request is in progress.'
    default:
      return 'Your tax preparer is working on your extension.'
  }
}

const getActionRequiredDescription = (
  substepIdentifier?: YearEndModuleSubstepId | null,
  paymentDueDate?: string | null
) => {
  const dueDate = convertUtcToLocalDate(paymentDueDate ?? undefined)
  const taxYear = (dueDate?.year ?? DateTime.now().year) - 1
  switch (substepIdentifier) {
    case ExtensionRequestJobSubstep.inProgressNeedsUserAction:
      return 'Your tax preparer has questions about your information.'
    case ExtensionRequestJobSubstep.updatePaymentInfo:
      return (
        <Grid>
          <GridRowColumn>
            <Text as="bodySm">
              <b>Your extension request has been filed.</b> For our records,
              please let us know if you owed any taxes. Check out{' '}
              <Link
                href="https://support.joinheard.com/hc/en-us/articles/31968570013335-After-tax-extension-filing"
                newPage
              >
                this article
              </Link>{' '}
              for guidance on how much to pay.
            </Text>
          </GridRowColumn>
          <GridRowColumn short>
            <Text as="bodySm">
              <b>Reminder:</b> Even with an extension, you may still need to pay
              an estimated tax liability by{' '}
              <b>
                {dueDate
                  ? dueDate.toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG)
                  : 'the IRS deadline'}
              </b>{' '}
              for any remaining taxes owed for {taxYear}. We recommend doing
              this first.
            </Text>
          </GridRowColumn>
        </Grid>
      )
    default:
      return ''
  }
}

const getDescription = (
  status: YearEndModuleStatusOptions,
  substepIdentifier?: YearEndModuleSubstepId | null,
  filedDate?: DateTime | null,
  paymentDueDate?: string | null
) => {
  const filedDateText = filedDate
    ? `${filedDate.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}`
    : ''
  switch (status) {
    case YearEndModuleStatusOptions.waitingOnHeard: {
      return getWaitingOnHeardDescription(substepIdentifier)
    }
    case YearEndModuleStatusOptions.actionRequired: {
      return getActionRequiredDescription(substepIdentifier, paymentDueDate)
    }
    case YearEndModuleStatusOptions.complete: {
      return (
        <>
          Successfully filed {filedDate && 'on'} <b>{filedDateText}</b>
        </>
      )
    }
    case YearEndModuleStatusOptions.missed: {
      return (
        <>
          Since you missed the deadline, we are no longer able to file an
          extension for you.
          <br />
          <br />
          You are now{' '}
          <b>at risk of incurring failure to file penalties and interest</b>.
          Please file your tax return(s) as soon as you can.
        </>
      )
    }
    case YearEndModuleStatusOptions.cancelled: {
      return 'Your extension request has been cancelled and will not be filed.'
    }
    case YearEndModuleStatusOptions.upcoming: {
      return (
        <UpcomingStatusTextWrapper
          moduleType={YearEndModuleType.fileExtensionRequest}
        />
      )
    }
    default:
      return null
  }
}

const getAlertBody = (
  dueDate: string | null,
  substepIdentifier: YearEndModuleSubstepId | null | undefined
) => {
  const daysUntilDueDate = getDeadlineApproachingDaysUntilDueDate(dueDate)
  if (daysUntilDueDate === null) {
    return null
  }
  const beforeDueDate = daysUntilDueDate >= 0
  const linkHref =
    'https://support.joinheard.com/hc/en-us/articles/19788518004887-Taxfyle-FAQs'
  const link = (
    <Link
      href={linkHref}
      newPage
      size="small"
      style={{
        textDecoration: 'underline',
        color: Colors.charcoal,
        textDecorationColor: Colors.charcoal,
      }}
    >
      More info
    </Link>
  )
  if (substepIdentifier === ExtensionRequestJobSubstep.updatePaymentInfo) {
    return null
  }
  if (beforeDueDate) {
    return (
      <Text as="bodySm">
        After that, you may be at risk of penalties and interest. Please
        complete your extension filing ASAP.
      </Text>
    )
  }
  return (
    <Text as="bodySm">
      The deadline for filing your extension request has passed. Please check
      with your tax preparer to see if your request has been filed on time.{' '}
      {link}
    </Text>
  )
}

const FileExtensionRequestCard = ({
  status,
  dueDate,
  taxYear,
  substepIdentifier,
  filedDateIso,
  paymentDueDate,
}: YearEndModuleStatusDynamicDataParams & {
  filedDateIso?: string | null
  paymentDueDate?: string | null
}) => {
  const filedDate = convertUtcToLocalDate(filedDateIso)
  const description = getDescription(
    status,
    substepIdentifier,
    filedDate,
    paymentDueDate
  )
  const { onActionClick, modal, loading, error } =
    useModuleActionButtonBehavior(
      YearEndModuleType.fileExtensionRequest,
      status,
      null, // substep url is not used for this module in year end home card
      substepIdentifier
    )
  // Don't show button for unclaimed job
  const showButtonOn =
    substepIdentifier === ExtensionRequestJobSubstep.unclaimedJob
      ? [
          YearEndModuleStatusOptions.upcoming,
          YearEndModuleStatusOptions.actionRequired,
        ]
      : [
          YearEndModuleStatusOptions.upcoming,
          YearEndModuleStatusOptions.actionRequired,
          YearEndModuleStatusOptions.waitingOnHeard,
        ]

  return (
    <>
      <YearEndModuleBaseCard
        key={YearEndModuleType.fileExtensionRequest}
        status={status}
        dueDate={dueDate}
        taxYear={taxYear}
        header={getModuleNameCopy(YearEndModuleType.fileExtensionRequest)}
        imageUrl="https://heard-images.s3.amazonaws.com/assets/tax-extension.svg"
        description={<Text as="bodySm">{description}</Text>}
        timer={{
          unit: 'days',
          value: '7-10',
          complementaryText: getModuleComplementaryText(
            YearEndModuleType.fileExtensionRequest
          ),
        }}
        deadlineApproaching={{
          ...defaultDeadlineApproaching,
          alertBody: getAlertBody(dueDate, substepIdentifier),
        }}
        button={{
          showOn: showButtonOn,
          onActionClick,
          loading,
          error,
          text:
            substepIdentifier === ExtensionRequestJobSubstep.updatePaymentInfo
              ? 'Update Now'
              : undefined,
        }}
      />
      {modal}
    </>
  )
}

export default FileExtensionRequestCard
